/*!
 * Girlbasket Theme v1.0.0
 * Copyright (c) 2026 Om. All rights reserved.
 *
 * This file is part of the Girlbasket theme, licensed only for use on the
 * Girlbasket store (girlbasket.myshopify.com / girlbasket.com). Copying,
 * modifying, redistributing or reselling this file, in whole or in part,
 * without written permission from Om is prohibited. See LICENSE.md.
 *
 * File: assets/gb-header.js
 */
(() => {
  'use strict';

  const GB = window.GB;
  if (!GB || !GB.events) return;

  /* ---------------------------------------------------------------------
     <gb-announcement>: rotating announcement slides
     --------------------------------------------------------------------- */
  class GbAnnouncement extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-gb-slide]')];
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      this.autoplay = this.dataset.autoplay === 'true' && !GB.media.reducedMotion.matches;
      this.speed = Number(this.dataset.speed) || 5000;
      this.userPaused = false;
      if (this.slides.length < 2) return;

      this.querySelector('[data-gb-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-gb-next]')?.addEventListener('click', () => this.go(this.index + 1));
      this.pauseButton = this.querySelector('[data-gb-pause]');
      this.pauseButton?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.pauseButton.setAttribute('aria-pressed', String(this.userPaused));
        this.userPaused ? this.stop() : this.start();
      });

      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());

      this.unsubscribe = GB.events.on('countdown:ended', ({ host }) => {
        if (!this.slides.includes(host)) return;
        this.slides = this.slides.filter((slide) => slide !== host);
        if (!this.slides.length) {
          this.closest('.gb-announcement')?.setAttribute('hidden', '');
          return;
        }
        this.go(this.index);
      });

      this.start();
    }

    disconnectedCallback() {
      this.stop();
      if (this.unsubscribe) this.unsubscribe();
    }

    go(next) {
      const count = this.slides.length;
      this.index = ((next % count) + count) % count;
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });
    }

    start() {
      if (!this.autoplay || this.userPaused || this.slides.length < 2) return;
      this.stop();
      this.timer = setInterval(() => this.go(this.index + 1), this.speed);
    }

    stop() {
      clearInterval(this.timer);
    }

    // Used by the theme editor to show a selected block.
    show(slide) {
      const i = this.slides.indexOf(slide);
      if (i < 0) return;
      this.stop();
      this.go(i);
    }
  }
  customElements.define('gb-announcement', GbAnnouncement);

  /* ---------------------------------------------------------------------
     <gb-site-header>: sticky state, header height, menu fit check
     --------------------------------------------------------------------- */
  class GbSiteHeader extends HTMLElement {
    connectedCallback() {
      this.nav = this.querySelector('.gb-header__nav');
      this.list = this.querySelector('.gb-nav__list');

      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this);

      if (this.hasAttribute('data-sticky')) {
        this.onScroll = () => {
          if (this.ticking) return;
          this.ticking = true;
          requestAnimationFrame(() => {
            this.classList.toggle('is-scrolled', window.scrollY > 4);
            this.ticking = false;
          });
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();
      }

      this.unsubscribe = GB.events.on('viewport:resize', () => this.checkFit());
      this.checkFit();
      document.fonts?.ready.then(() => this.checkFit());
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      if (this.unsubscribe) this.unsubscribe();
    }

    measure() {
      document.documentElement.style.setProperty('--gb-header-height', `${Math.round(this.offsetHeight)}px`);
    }

    // When the desktop menu does not fit on one line, use the mobile menu instead.
    checkFit() {
      if (!this.nav || !this.list) return;
      this.classList.remove('gb-header--compact');
      if (!GB.isDesktop()) return;
      if (this.list.scrollWidth > this.nav.clientWidth + 1) this.classList.add('gb-header--compact');
    }
  }
  customElements.define('gb-site-header', GbSiteHeader);

  /* ---------------------------------------------------------------------
     <gb-nav>: desktop dropdowns and mega menus
     --------------------------------------------------------------------- */
  class GbNav extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('[data-gb-dropdown]')];
      this.items.forEach((item) => {
        const toggle = item.querySelector('[data-gb-dropdown-toggle]');
        toggle.addEventListener('click', () => {
          this.isOpen(item) ? this.close(item) : this.open(item);
        });

        item.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse') return;
          clearTimeout(item.closeTimer);
          this.open(item);
        });
        item.addEventListener('pointerleave', (event) => {
          if (event.pointerType !== 'mouse') return;
          item.closeTimer = setTimeout(() => this.close(item), 160);
        });
        item.addEventListener('focusout', (event) => {
          if (!item.contains(event.relatedTarget)) this.close(item);
        });
        item.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape' || !this.isOpen(item)) return;
          event.stopPropagation();
          this.close(item);
          toggle.focus();
        });
      });

      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.items.forEach((item) => this.close(item));
      };
      document.addEventListener('click', this.onDocumentClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
    }

    isOpen(item) {
      return item.classList.contains('is-open');
    }

    open(item) {
      this.items.forEach((other) => other !== item && this.close(other));
      const panel = item.querySelector('[data-gb-dropdown-panel]');
      panel.hidden = false;
      item.classList.add('is-open');
      item.querySelector('[data-gb-dropdown-toggle]').setAttribute('aria-expanded', 'true');
      this.closest('gb-site-header')?.classList.add('has-open-menu');

      // Keep small dropdowns inside the screen.
      if (!panel.classList.contains('gb-dropdown--mega')) {
        panel.classList.remove('is-flipped');
        if (panel.getBoundingClientRect().right > document.documentElement.clientWidth - 8) {
          panel.classList.add('is-flipped');
        }
      }
    }

    close(item) {
      if (!this.isOpen(item)) return;
      item.classList.remove('is-open');
      item.querySelector('[data-gb-dropdown-panel]').hidden = true;
      item.querySelector('[data-gb-dropdown-toggle]').setAttribute('aria-expanded', 'false');
      if (!this.querySelector('.is-open')) this.closest('gb-site-header')?.classList.remove('has-open-menu');
    }
  }
  customElements.define('gb-nav', GbNav);

  /* ---------------------------------------------------------------------
     <gb-menu-levels>: sliding levels in the mobile menu drawer
     --------------------------------------------------------------------- */
  class GbMenuLevels extends HTMLElement {
    connectedCallback() {
      this.root = this.querySelector('[data-gb-level]');
      this.stack = [];

      this.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-gb-level-open]');
        if (opener) {
          this.forward(opener);
          return;
        }
        if (event.target.closest('[data-gb-level-back]')) this.back();
      });

      this.unsubscribe = GB.events.on('drawer:close', ({ drawer }) => {
        if (drawer.contains(this)) this.reset();
      });
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    get current() {
      return this.stack.length ? this.stack[this.stack.length - 1].panel : this.root;
    }

    forward(opener) {
      const panel = this.querySelector(`#${CSS.escape(opener.dataset.gbLevelOpen)}`);
      if (!panel) return;
      const from = this.current;
      opener.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      requestAnimationFrame(() => {
        from.classList.add('is-behind');
        from.classList.remove('is-active');
        panel.classList.add('is-active');
        panel.querySelector('[data-gb-level-back]')?.focus({ preventScroll: true });
      });
      this.stack.push({ panel, opener, from });
    }

    back() {
      const step = this.stack.pop();
      if (!step) return;
      step.panel.classList.remove('is-active');
      step.from.classList.remove('is-behind');
      step.from.classList.add('is-active');
      step.opener.setAttribute('aria-expanded', 'false');
      step.opener.focus({ preventScroll: true });
      setTimeout(() => {
        if (!step.panel.classList.contains('is-active')) step.panel.hidden = true;
      }, 300);
    }

    reset() {
      this.stack.forEach(({ panel, opener }) => {
        panel.classList.remove('is-active', 'is-behind');
        panel.hidden = true;
        opener.setAttribute('aria-expanded', 'false');
      });
      this.stack = [];
      this.root.classList.remove('is-behind');
      this.root.classList.add('is-active');
    }
  }
  customElements.define('gb-menu-levels', GbMenuLevels);

  /* ---------------------------------------------------------------------
     Footer menus fold on mobile, stay open on desktop
     --------------------------------------------------------------------- */
  const syncFooterFolds = () => {
    document.querySelectorAll('[data-gb-footer-fold]').forEach((fold) => {
      fold.open = GB.media.tablet.matches;
    });
  };
  syncFooterFolds();
  GB.media.tablet.addEventListener('change', syncFooterFolds);
  document.addEventListener('shopify:section:load', syncFooterFolds);
})();
